import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "itemsTbody", "itemRowTemplate", "itemRow",
    "itemName", "itemQty", "itemPrice",
    "totalAmount",
    "fundingProgramContainer", "requirementContainer",
    "ppsHint",
    "notifyOnClaim", "notes", "recipientEmail",
    "copyLinkBtn", "sendEmailBtn",
    "formStep", "resultStep",
    "formActions", "resultActions",
    "linkInput", "linkContainer", "copyBtn", "copyBtnText",
    "resultCard", "resultIcon", "resultKicker", "resultTitle",
    "successMessage", "linkTitle", "linkSubtitle"
  ];

  static values = {
    url: String,
    applicationsUrl: String,
    fundingProgramsUrl: String,
    requirementsUrl: String
  };

  connect() {
    this._boundReset = this._reset.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundReset);

    this._boundUser = this.onUserChange.bind(this);
    this._boundFp = this.onFundingProgramChange.bind(this);
    this._boundReq = this.onRequirementChange.bind(this);
    this._boundAccessLevel = this.onAccessLevelChange.bind(this);
    this._accessLevelAutoSelected = true;
    this._userInput?.addEventListener("app-select:change", this._boundUser);
    this._fpInput?.addEventListener("app-select:change", this._boundFp);
    this._reqInput?.addEventListener("app-select:change", this._boundReq);
    this.accessLevelInput?.addEventListener("app-select:change", this._boundAccessLevel);

    this.addItem();
    this.recalcTotal();
    this._updateContainerVisibility();
    this._syncAccessLevelForUser();
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundReset);
    this._userInput?.removeEventListener("app-select:change", this._boundUser);
    this._fpInput?.removeEventListener("app-select:change", this._boundFp);
    this._reqInput?.removeEventListener("app-select:change", this._boundReq);
    this.accessLevelInput?.removeEventListener("app-select:change", this._boundAccessLevel);
  }

  // ── Cascading selects ────────────────────────────────────────────────

  async onUserChange(event) {
    const userId = this._userInput?.value || "";

    this._syncRecipientEmailForUser(event?.detail?.email);
    this._setOptions(this._reqInput, [], "Select a funding program first");
    this._togglePpsHint(false);
    this._clearItems();
    this._syncAccessLevelForUser();

    if (!userId) {
      this._setOptions(this._fpInput, [], "— None —");
      this._updateContainerVisibility();
      this._updateSubmitState();
      return;
    }

    this._updateContainerVisibility();
    this._updateSubmitState();

    const programs = await this._fetchJson(this.fundingProgramsUrlValue, { user_id: userId });
    if (Array.isArray(programs)) {
      this._setOptions(
        this._fpInput,
        programs.map(p => [p.name, String(p.id)]),
        programs.length ? "— None —" : "No funding programs for this user"
      );
    }
  }

  async onFundingProgramChange() {
    const programId = this._fpInput?.value || "";

    this._setOptions(this._reqInput, [], "Select a funding program first");
    this._togglePpsHint(false);
    this._clearItems();

    if (!programId) {
      this._updateContainerVisibility();
      this._updateSubmitState();
      return;
    }

    const requirements = await this._fetchJson(this.requirementsUrlValue, {
      funding_program_id: programId,
      user_id: this._userInput?.value || ""
    });

    if (Array.isArray(requirements)) {
      this._lastRequirements = requirements;
      this._setOptions(
        this._reqInput,
        requirements.map(r => [r.name, String(r.id)]),
        "— None —"
      );
    }

    this._updateContainerVisibility();
    this._updateSubmitState();
  }

  onRequirementChange() {
    const reqId = this._reqInput?.value || "";
    if (!reqId || !this._lastRequirements) {
      this._togglePpsHint(false);
      this._clearItems();
      this._updateSubmitState();
      return;
    }

    const req = this._lastRequirements.find(r => String(r.id) === String(reqId));
    if (!req) return;

    this.itemsTbodyTarget.innerHTML = "";
    let hasExisting = false;
    req.items.filter(i => !i.sold_out).forEach(item => {
      this.addItem();
      const rows = this.itemRowTargets;
      const row = rows[rows.length - 1];
      row.querySelector('[data-payment-link-modal-target="itemName"]').value = item.name;
      row.querySelector('[data-payment-link-modal-target="itemQty"]').value = 0;
      row.querySelector('[data-payment-link-modal-target="itemPrice"]').value = item.unit_price.toFixed(2);
      const existing = item.existing_quantity || 0;
      if (existing > 0) hasExisting = true;
      this._setExistingQtyHint(row, existing);
    });

    if (this.itemRowTargets.length === 0) {
      this.addItem();
    }

    this._togglePpsHint(hasExisting);
    this.recalcTotal();
  }

  onAccessLevelChange() {
    this._accessLevelAutoSelected = false;
    this._updateSubmitState();
  }

  updateSubmitState() {
    this._updateSubmitState();
  }

  // ── Items table ──────────────────────────────────────────────────────

  addItem() {
    const tmpl = this.itemRowTemplateTarget.content.cloneNode(true);
    this.itemsTbodyTarget.appendChild(tmpl);
    this.recalcTotal();
  }

  _clearItems() {
    this.itemsTbodyTarget.innerHTML = "";
    this.addItem();
  }

  removeItem(event) {
    const row = event.currentTarget.closest("tr");
    if (row) row.remove();
    this.recalcTotal();
  }

  recalcTotal() {
    let total = 0;
    this.itemRowTargets.forEach(row => {
      const qty = parseInt(row.querySelector('[data-payment-link-modal-target="itemQty"]').value, 10) || 0;
      const price = parseFloat(row.querySelector('[data-payment-link-modal-target="itemPrice"]').value) || 0;
      total += qty * price;
    });
    this.totalAmountTarget.textContent = total.toFixed(2);
    this._updateSubmitState();
  }

  // ── Submit ───────────────────────────────────────────────────────────

  async generateLink() {
    await this._submit("copy_link");
  }

  async sendEmail() {
    await this._submit("send_email");
  }

  copyLink(event) {
    event.preventDefault();
    const url = this.linkInputTarget.value;
    if (!url) return;

    navigator.clipboard.writeText(url).then(() => {
      this._setCopyButtonText("Copied!");
      setTimeout(() => { this._setCopyButtonText("Copy"); }, 1500);
    }).catch(() => {
      this.linkInputTarget.select();
      document.execCommand("copy");
    });
  }

  // ── Private ──────────────────────────────────────────────────────────

  _collectItems() {
    return this.itemRowTargets.map(row => {
      const name = row.querySelector('[data-payment-link-modal-target="itemName"]').value.trim();
      const qty = parseInt(row.querySelector('[data-payment-link-modal-target="itemQty"]').value, 10) || 0;
      const price = parseFloat(row.querySelector('[data-payment-link-modal-target="itemPrice"]').value) || 0;
      return { name, quantity: qty, unit_price: price };
    }).filter(i => i.name && i.quantity > 0);
  }

  _updateSubmitState() {
    const items = this._collectItems();
    const hasItems = items.length > 0;
    const total = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
    const hasUser = !!(this._userInput && this._userInput.value);
    const hasEmailRecipient = this._validRecipientEmail();
    const canCreateLink = hasItems && total > 0;
    this.copyLinkBtnTarget.disabled = !canCreateLink;
    this.sendEmailBtnTarget.disabled = !(canCreateLink && (hasUser || hasEmailRecipient));
  }

  async _submit(actionType) {
    const items = this._collectItems();
    if (items.length === 0) return;

    const hasUser = !!(this._userInput && this._userInput.value);
    const recipientEmail = this._recipientEmailValue();
    if (actionType === "send_email" && !hasUser && !this._validRecipientEmail()) {
      alert("Enter a recipient email address to send this payment link.");
      return;
    }

    const payload = {
      payment_link: {
        recipient_id: this._userInput?.value || null,
        recipient_email: recipientEmail || null,
        funding_program_id: this._fpInput?.value || null,
        funding_program_requirement_id: this._reqInput?.value || null,
        access_level: this._accessLevelValue(),
        notify_on_claim: this.notifyOnClaimTarget.checked ? "1" : "0",
        notes: this.notesTarget.value || null,
        action_type: actionType,
        items: items
      }
    };

    this._setLoading(true);
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        this._showResult(data, actionType);
      } else {
        alert(data.errors?.join(", ") || "An error occurred.");
        this._setLoading(false);
      }
    } catch (e) {
      alert("An error occurred. Please try again.");
      this._setLoading(false);
    }
  }

  _showResult(data, actionType) {
    this.formStepTarget.classList.add("d-none");
    this.resultStepTarget.classList.remove("d-none");
    this.formActionsTarget.classList.add("d-none");
    this.resultActionsTarget.classList.remove("d-none");

    this.linkInputTarget.value = data.url;
    this.linkContainerTarget.classList.remove("d-none");

    if (actionType === "send_email") {
      this._configureResult({
        variant: "email",
        iconClass: "ph-envelope",
        kicker: "Sent by email",
        title: "Payment link emailed",
        message: data.message || "The recipient will receive this payment link by email.",
        linkTitle: "Fallback copy",
        linkSubtitle: "Use this link if the recipient needs it resent another way."
      });
    } else {
      this._configureResult({
        variant: "copy",
        iconClass: "ph-copy",
        kicker: "Copied to clipboard",
        title: "Payment link ready to paste",
        message: "The payment link has been created and copied for sharing.",
        linkTitle: "Copyable link",
        linkSubtitle: "This is the shareable payment URL."
      });
      if (data.url) {
        navigator.clipboard.writeText(data.url).then(() => {
          this._setCopyButtonText("Copied!");
          setTimeout(() => { this._setCopyButtonText("Copy"); }, 1500);
        }).catch(() => {});
      }
    }
  }

  _configureResult({ variant, iconClass, kicker, title, message, linkTitle, linkSubtitle }) {
    this.resultCardTarget.classList.remove("is-copy", "is-email");
    this.resultCardTarget.classList.add(`is-${variant}`);
    this.resultIconTarget.className = `ph ${iconClass}`;
    this.resultKickerTarget.textContent = kicker;
    this.resultTitleTarget.textContent = title;
    this.successMessageTarget.textContent = message;
    this.linkTitleTarget.textContent = linkTitle;
    this.linkSubtitleTarget.textContent = linkSubtitle;
  }

  _setCopyButtonText(text) {
    if (this.hasCopyBtnTextTarget) {
      this.copyBtnTextTarget.textContent = text;
    } else {
      this.copyBtnTarget.textContent = text;
    }
  }

  _setLoading(loading) {
    if (loading) {
      this.copyLinkBtnTarget.disabled = true;
      this.sendEmailBtnTarget.disabled = true;
    } else {
      this._updateSubmitState();
    }
  }

  async _fetchJson(url, params) {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${url}?${qs}`, {
      headers: { "Accept": "application/json" }
    });
    if (!response.ok) return [];
    return response.json();
  }

  _reset() {
    this._resetSelect(this._userInput, "Select a user (optional)");
    this._setOptions(this._fpInput, [], "— None —");
    this._setOptions(this._reqInput, [], "Select a funding program first");
    this._togglePpsHint(false);

    this.itemsTbodyTarget.innerHTML = "";
    this.addItem();

    this._resetAccessLevel();
    this._accessLevelAutoSelected = true;
    if (this.hasRecipientEmailTarget) this.recipientEmailTarget.value = "";
    this.notifyOnClaimTarget.checked = false;
    this.notesTarget.value = "";

    this.formStepTarget.classList.remove("d-none");
    this.resultStepTarget.classList.add("d-none");
    this.formActionsTarget.classList.remove("d-none");
    this.resultActionsTarget.classList.add("d-none");
    this.linkInputTarget.value = "";
    this.resultCardTarget.classList.remove("is-copy", "is-email");
    this.resultIconTarget.className = "ph ph-link";
    this.resultKickerTarget.textContent = "";
    this.resultTitleTarget.textContent = "";
    this.successMessageTarget.textContent = "";
    this.linkTitleTarget.textContent = "";
    this.linkSubtitleTarget.textContent = "";
    this.linkContainerTarget.classList.remove("d-none");
    this._setCopyButtonText("Copy");

    this.recalcTotal();
    this._updateContainerVisibility();
  }

  _updateContainerVisibility() {
    const hasUser = !!(this._userInput && this._userInput.value);
    const hasFp = !!(this._fpInput && this._fpInput.value);
    if (this.hasFundingProgramContainerTarget) {
      this.fundingProgramContainerTarget.classList.toggle("d-none", !hasUser);
    }
    if (this.hasRequirementContainerTarget) {
      this.requirementContainerTarget.classList.toggle("d-none", !hasUser || !hasFp);
    }
  }

  _togglePpsHint(show) {
    if (this.hasPpsHintTarget) {
      this.ppsHintTarget.classList.toggle("d-none", !show);
    }
  }

  _setExistingQtyHint(row, quantity) {
    const wrapper = row.querySelector(".qty-cell");
    if (!wrapper) return;
    wrapper.querySelectorAll(".existing-qty-hint").forEach(el => el.remove());
    if (quantity > 0) {
      const hint = document.createElement("span");
      hint.className = "existing-qty-hint";
      hint.title = `${quantity} already in this user's response`;
      hint.innerHTML = `<i class="ph ph-check-circle"></i><span>Existing: <strong>${quantity}</strong></span>`;
      wrapper.appendChild(hint);
    }
  }

  // ── app-select helpers ────────────────────────────────────

  get _userInput() {
    return this.element.querySelector('input[name="payment_link[recipient_id]"]');
  }
  get _fpInput() {
    return this.element.querySelector('input[name="payment_link[funding_program_id]"]');
  }
  get _reqInput() {
    return this.element.querySelector('input[name="payment_link[funding_program_requirement_id]"]');
  }

  _setOptions(hiddenInput, options, prompt) {
    if (!hiddenInput) return;
    const wrapper = hiddenInput.parentElement; // .app-select
    const panel = wrapper.querySelector('.app-select-panel');
    const label = wrapper.querySelector('[data-app-select-target="label"]');
    const search = panel.querySelector('.app-select-search');

    // Remove all existing option buttons & groups (preserve search input if any).
    panel.querySelectorAll('.app-select-option, .app-select-group').forEach(el => el.remove());

    options.forEach(([optLabel, optValue]) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'app-select-option';
      btn.dataset.value = optValue;
      btn.dataset.label = optLabel;
      btn.setAttribute('data-app-select-target', 'option');
      btn.setAttribute(
        'data-action',
        'click->app-select#selectOption keydown->app-select#listKeydown'
      );
      const span = document.createElement('span');
      span.textContent = optLabel;
      btn.appendChild(span);
      panel.appendChild(btn);
    });

    hiddenInput.value = "";
    if (label) label.textContent = prompt;
  }

  _setSelectDisabled(hiddenInput, disabled) {
    if (!hiddenInput) return;
    const wrapper = hiddenInput.parentElement; // .app-select
    if (!wrapper) return;
    wrapper.style.pointerEvents = disabled ? "none" : "";
    wrapper.style.opacity = disabled ? "0.6" : "";
  }

  _resetSelect(hiddenInput, prompt) {
    if (!hiddenInput) return;
    hiddenInput.value = "";
    const wrapper = hiddenInput.parentElement;
    if (!wrapper) return;
    const label = wrapper.querySelector('[data-app-select-target="label"]');
    if (label) label.textContent = prompt;
    wrapper.querySelectorAll(".app-select-option").forEach((el) => {
      el.classList.remove("app-select-option-selected");
    });
  }

  get accessLevelInput() {
    return this.element.querySelector('input[name="payment_link[access_level]"]');
  }

  _accessLevelValue() {
    const value = this.accessLevelInput?.value || "anyone";
    if (value === "recipient" && !(this._userInput && this._userInput.value)) return "anyone";
    return value;
  }

  _resetAccessLevel() {
    this._setAccessLevel("anyone");
  }

  _syncAccessLevelForUser() {
    const hasUser = !!(this._userInput && this._userInput.value);
    const value = this.accessLevelInput?.value || "anyone";

    if (!hasUser && value === "recipient") {
      this._accessLevelAutoSelected = true;
      this._setAccessLevel("anyone");
    } else if (hasUser && this._accessLevelAutoSelected && value === "anyone") {
      this._setAccessLevel("recipient");
    }
  }

  _setAccessLevel(value) {
    const input = this.accessLevelInput;
    if (!input) return;
    const labels = {
      recipient: "Selected User only",
      logged_in_user: "Any authenticated User",
      anyone: "Anyone with the link"
    };

    input.value = value;
    const container = input.closest(".app-select-container");
    if (!container) return;
    const label = container.querySelector('[data-app-select-target="label"]');
    if (label) label.textContent = labels[value] || labels.anyone;
    container.querySelectorAll(".app-select-option").forEach((el) => {
      el.classList.toggle("app-select-option-selected", el.dataset.value === value);
    });
  }

  _recipientEmailValue() {
    return this.hasRecipientEmailTarget ? this.recipientEmailTarget.value.trim() : "";
  }

  _validRecipientEmail() {
    const email = this._recipientEmailValue();
    return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  _syncRecipientEmailForUser(email) {
    if (!this.hasRecipientEmailTarget) return;

    if (email) {
      this.recipientEmailTarget.value = email;
    } else if (!(this._userInput && this._userInput.value)) {
      this.recipientEmailTarget.value = "";
    }
  }
}
